/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import type { DayRange } from './daySelectorUtils';
import {
  getNextFocus,
  isSameRange,
  normaliseRange,
  resolveDayFromClientX,
} from './daySelectorUtils';

export interface UseDaySelectionParams {
  selectedDays: DayRange;
  onChange: (range: DayRange | null) => void;
  isDisabled?: boolean;
}

export interface UseDaySelectionResult {
  containerRef: React.RefObject<HTMLDivElement | null>;
  focusedDay: number;
  previewRange: DayRange | null;
  onPointerDown: (day: number, event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerCancel: (event: React.PointerEvent) => void;
  onKeyDown: (day: number, event: React.KeyboardEvent) => void;
  onChipFocus: (day: number) => void;
}

export const useDaySelection = ({
  selectedDays,
  onChange,
  isDisabled = false,
}: UseDaySelectionParams): UseDaySelectionResult => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [previewRange, setPreviewRange] = React.useState<DayRange | null>(null);
  const [focusedDay, setFocusedDay] = React.useState(selectedDays[1]);
  const isDraggingRef = React.useRef(false);
  const pointerIdRef = React.useRef<number | null>(null);
  const lastEmittedRef = React.useRef<DayRange | null>(null);
  // the anchor is the day the selection started from: it stays put while the
  // focused day moves, so extending keeps growing instead of flipping ends.
  // both are mirrored in refs so a key repeat or fast drag never reads a stale value
  const anchorRef = React.useRef(selectedDays[0]);
  const focusedDayRef = React.useRef(focusedDay);

  const focusDay = (day: number): void => {
    focusedDayRef.current = day;
    setFocusedDay(day);
  };

  const [rangeStart, rangeEnd] = selectedDays;

  // a range set from outside (a default reset, a warning type change) re-anchors
  React.useEffect(() => {
    const range: DayRange = [rangeStart, rangeEnd];
    if (lastEmittedRef.current && isSameRange(lastEmittedRef.current, range)) {
      return;
    }
    lastEmittedRef.current = null;
    anchorRef.current = rangeStart;
    focusedDayRef.current = rangeEnd;
    setFocusedDay(rangeEnd);
  }, [rangeStart, rangeEnd]);

  const commit = (range: DayRange, canClear = true): void => {
    const shouldClear = canClear && isSameRange(range, selectedDays);
    lastEmittedRef.current = shouldClear ? null : range;
    onChange(shouldClear ? null : range);
  };

  const endDrag = React.useCallback((): void => {
    if (pointerIdRef.current !== null) {
      containerRef.current?.releasePointerCapture?.(pointerIdRef.current);
    }
    pointerIdRef.current = null;
    isDraggingRef.current = false;
    setPreviewRange(null);
  }, []);

  React.useEffect(() => {
    if (previewRange === null) {
      return undefined;
    }
    const onEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        endDrag();
      }
    };
    window.addEventListener('keydown', onEscape);
    return (): void => window.removeEventListener('keydown', onEscape);
  }, [previewRange, endDrag]);

  const onPointerDown = (day: number, event: React.PointerEvent): void => {
    if (isDisabled) {
      return;
    }
    event.preventDefault();
    const nextAnchor = event.shiftKey ? anchorRef.current : day;
    anchorRef.current = nextAnchor;
    focusDay(day);
    isDraggingRef.current = true;
    pointerIdRef.current = event.pointerId;
    containerRef.current?.setPointerCapture?.(event.pointerId);
    setPreviewRange(normaliseRange(nextAnchor, day));
    // the drag needs focus on the pressed chip: preventDefault stops the browser
    // from moving it there itself, and the shortcuts are useless without it
    (event.currentTarget as HTMLElement).focus();
  };

  const onPointerMove = (event: React.PointerEvent): void => {
    if (!isDraggingRef.current) {
      return;
    }
    const containerLeft =
      containerRef.current?.getBoundingClientRect().left ?? 0;
    const day = resolveDayFromClientX(event.clientX, containerLeft);
    focusDay(day);
    setPreviewRange(normaliseRange(anchorRef.current, day));
  };

  const onPointerUp = (): void => {
    if (previewRange) {
      commit(previewRange);
    }
    endDrag();
  };

  const onKeyDown = (day: number, event: React.KeyboardEvent): void => {
    if (isDisabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      anchorRef.current = day;
      focusDay(day);
      commit([day, day]);
      return;
    }
    const nextFocus = getNextFocus(focusedDayRef.current, event.key);
    if (nextFocus === null) {
      return;
    }
    event.preventDefault();
    focusDay(nextFocus);
    if (event.shiftKey) {
      commit(normaliseRange(anchorRef.current, nextFocus), false);
    }
  };

  return {
    containerRef,
    focusedDay,
    previewRange,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: endDrag,
    onKeyDown,
    onChipFocus: focusDay,
  };
};
